class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    this.cancelPendingFocus();
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      // Replacing an already-open drawer produces no transitionend event.
      // Wait for the visibility transition to leave its hidden starting state,
      // while still covering already-open and reduced-motion drawers.
      const focusCurrentDrawer = () => {
        if (!this.classList.contains('active')) return;
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        if (!container) return;
        const focusElement = container.contains(document.activeElement)
          ? document.activeElement
          : container.querySelector('.drawer__close') || this.querySelector('.drawer__inner') || container;
        trapFocus(container, focusElement);
        if (container.contains(document.activeElement)) this.cancelPendingFocus();
      };
      this.focusTransitionHandler = (event) => {
        if (event.target === this || event.target === this.querySelector('.drawer__inner')) focusCurrentDrawer();
      };
      this.addEventListener('transitionend', this.focusTransitionHandler);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = requestAnimationFrame(focusCurrentDrawer);
      });
      this.focusFallbackTimer = setTimeout(focusCurrentDrawer, 400);
    });
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    this.cancelPendingFocus();
    this.classList.remove('active');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  cancelPendingFocus() {
    cancelAnimationFrame(this.focusFrame);
    clearTimeout(this.focusFallbackTimer);
    if (this.focusTransitionHandler) {
      this.removeEventListener('transitionend', this.focusTransitionHandler);
      this.focusTransitionHandler = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const updates = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      const source = typeof html === 'string' ? this.getSectionDOM(html, section.selector) : null;
      return { sectionElement, source };
    });
    // Shopify can confirm the add while returning null section HTML. Show the
    // authoritative cart without repeating the add or partially replacing its UI.
    if (updates.some(({ sectionElement, source }) => !sectionElement || !source)) {
      window.location.assign(window.routes.cart_url);
      return false;
    }
    const drawerSource = updates[0].source.closest('cart-drawer');
    this.classList.toggle('is-empty', drawerSource?.classList.contains('is-empty') || false);
    this.productId = parsedState.id;
    updates.forEach(({ sectionElement, source }) => { sectionElement.innerHTML = source.innerHTML; });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
